import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { syncAppBadge, deviceNotify, enablePush, resyncPush } from "@/lib/appBadge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, BellRing, CheckCheck } from "lucide-react";
import { toast } from "sonner";

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");
  const prevUnread = useRef(null);

  const load = useCallback(() => {
    api.get("/notifications").then((r) => {
      const d = r.data || { items: [], unread: 0 };
      setData(d);
      // PWA app-icon badge: mirrors the unread count on the installed app icon.
      syncAppBadge(d.unread);
      // Device notification when a NEW actionable notification arrives while the app is open.
      if (prevUnread.current !== null && d.unread > prevUnread.current && perm === "granted") {
        const newest = (d.items || []).find((n) => !n.read);
        deviceNotify(
          "LiveLife Education Intranet",
          newest ? `${newest.title || "New update"}${newest.stage ? ` — moved to ${newest.stage}` : ""}` : `You have ${d.unread} unread notifications`
        );
      }
      prevUnread.current = d.unread;
    }).catch(() => {});
  }, [perm]);

  useEffect(() => {
    load();
    const i = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(i); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const enableDeviceNotifications = async () => {
    try {
      await enablePush(); // permission prompt + web push subscription (works when app is closed)
      setPerm("granted");
      toast.success("Notifications enabled — you'll get alerts even when the app is closed.");
    } catch (e) {
      setPerm(typeof Notification !== "undefined" ? Notification.permission : "denied");
      if (e?.message === "not-supported") toast.error("This browser does not support push notifications. Install the app to your home screen first.");
      else if (e?.message !== "denied") toast.error("Could not enable notifications.");
    }
  };

  // Self-heal: re-register an existing push subscription on every load.
  useEffect(() => { resyncPush(); }, []);

  const openItem = async (n) => {
    await api.post(`/notifications/${n.notif_id}/read`).catch(() => {});
    setOpen(false);
    load();
    const target = n.type === "social" ? "/calendar"
      : n.type === "tracker" || n.type === "leave" ? "/daily-tracker"
      : n.type === "announcement" || n.type === "holiday" ? "/home"
      : "/workflow";
    navigate(target);
  };

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative rounded-full h-9 w-9 flex items-center justify-center hover:bg-accent">
          <Bell className="h-4.5 w-4.5" />
          {data.unread > 0 && (
            <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="font-display font-semibold text-sm">Notifications</p>
          <div className="flex items-center gap-3">
            {perm === "default" && (
              <button onClick={enableDeviceNotifications} data-testid="enable-device-notifications" className="text-xs text-primary flex items-center gap-1">
                <BellRing className="h-3.5 w-3.5" /> Enable alerts
              </button>
            )}
            {data.items.length > 0 && (
              <button onClick={markAll} data-testid="mark-all-read" className="text-xs text-primary flex items-center gap-1"><CheckCheck className="h-3.5 w-3.5" /> Mark all read</button>
            )}
          </div>
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {data.items.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">You're all caught up.</p>}
          {data.items.map((n) => (
            <button key={n.notif_id} data-testid={`notif-${n.notif_id}`} onClick={() => openItem(n)}
              className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-accent ${!n.read ? "bg-primary/5" : ""}`}>
              <p className="text-sm font-medium leading-snug">
                {n.work_item_id && <span className="font-mono text-primary text-xs">{n.work_item_id} · </span>}{n.title}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {n.stage ? <>Moved to <span className="font-semibold">{n.stage}</span></> : n.type === "social" ? "Social Media update" : ["announcement", "tracker", "leave", "holiday"].includes(n.type) ? (n.message || "Update") : "Update"}
                {n.due_date ? ` · due ${fmtDate(n.due_date)}` : ""}
                <span className="ml-1">· {fmtDate(n.created_at)}</span>
              </p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
